import { Button, Icon } from '@sgme/ui';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { useAppSelector } from 'hooks/useAppSelector';
import { ExpandableBlockDefinition } from 'models/metadata/block.definition';
import { PropsWithChildren } from 'react';
import { useDispatch } from 'react-redux';
import { ErrorBadge } from 'routes/essential-workspace/components/form/blocks/error-badge';
import { EssentialBlockNode } from 'routes/essential-workspace/state/model';
import { essentialFormInfoProductSubtype, essentialFormInfoProductType } from 'state/essential/infos/selectors';
import { userPreferencesEssentialExpandedBlocksToggle } from 'state/userPreferences/userPreferences.action';

type ExpandableBlockProps = PropsWithChildren<EssentialBlockNode & ExpandableBlockDefinition>;

export function ExpandableBlockItem({ id, children, isExpanded = false, errors }: ExpandableBlockProps) {
  const { translateLabel } = useTranslateForm();
  const label = translateLabel(id);
  const collapseLabel = translateLabel(isExpanded ? 'hideAdditionalConfigs' : 'showAdditionalConfigs');

  const tabId = useAssertedActiveTabId();
  const productType = useAppSelector(state => essentialFormInfoProductType(state, tabId));
  const productSubType = useAppSelector(state => essentialFormInfoProductSubtype(state, tabId));

  const dispatch = useDispatch();

  const toggleExpend = () => {
    dispatch(userPreferencesEssentialExpandedBlocksToggle(id, productType, productSubType));
  };

  return (
    <section className="grid gap-2" style={{ gridTemplateRows: 'masonry' }} data-block-id={id} aria-label={label}>
      <header className="flex-x pt-2 g-col-12" style={{ height: 32 }}>
        <div className="flex-x gap-2 flex-grow-1">
          <h2 className="text-start fs-14 text-secondary fw-normal m-0 ">{label}</h2>
          <ErrorBadge errors={errors} />
        </div>
        <Button
          purpose="flat"
          color="secondary"
          iconPosition={'end'}
          style={{ width: 'fit-content', minWidth: 'auto' }}
          onClick={toggleExpend}
        >
          {collapseLabel}
          <Icon size="sm" aria-hidden="true" icon={isExpanded ? 'unfold_less' : 'unfold_more'} />
        </Button>
      </header>
      {children}
    </section>
  );
}
